import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { downloadPdf } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSignature, Plus, Download, Eye, PenLine, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Agreements() {
  const [agreements, setAgreements] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const [genJob, setGenJob] = useState("");
  const [genScope, setGenScope] = useState("");

  const [editor, setEditor] = useState(null); // agreement being viewed/edited
  const [scope, setScope] = useState("");
  const [terms, setTerms] = useState("");
  const [signName, setSignName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, j] = await Promise.all([api.get("/agreements"), api.get("/jobs")]);
      setAgreements(a.data); setJobs(j.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const jid = params.get("job");
    if (jid) { setGenJob(jid); setParams({}, { replace: true }); }
  }, [params]); // eslint-disable-line

  const generate = async () => {
    if (!genJob) return toast.error("Select a job");
    try {
      const res = await api.post("/agreements/generate", { job_id: genJob, scope_summary: genScope });
      toast.success("Agreement draft generated");
      setGenScope("");
      await load();
      openEditor(res.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not generate"); }
  };

  const openEditor = (a) => { setEditor(a); setScope(a.scope_summary || ""); setTerms(a.terms_text || ""); setSignName(a.signed_by_name || ""); setAck(a.status === "signed"); };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/agreements/${editor.id}`, { scope_summary: scope, terms_text: terms });
      setEditor(res.data); toast.success("Draft saved"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const sign = async () => {
    if (!signName.trim()) return toast.error("Type the signer's full name");
    if (!ack) return toast.error("Please acknowledge the terms");
    setBusy(true);
    try {
      if (editor.status !== "signed") await api.put(`/agreements/${editor.id}`, { scope_summary: scope, terms_text: terms });
      const res = await api.post(`/agreements/${editor.id}/sign`, { signed_by_name: signName });
      toast.success("Agreement signed & locked");
      await load();
      await downloadPdf(`/agreements/${res.data.id}/pdf`, `agreement-${res.data.id}.pdf`);
      setEditor(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not sign"); }
    finally { setBusy(false); }
  };

  const dl = async (a) => { try { await downloadPdf(`/agreements/${a.id}/pdf`, `agreement-${a.id}.pdf`); } catch { toast.error("Download failed"); } };

  const jobsWithout = jobs.filter((j) => !agreements.some((a) => a.job_id === j.id && a.status === "signed"));
  const signed = editor?.status === "signed";

  return (
    <div>
      <PageHeader title="Agreements" subtitle="Auto-generate a contract from a job, then type-to-sign to lock it as a PDF." />

      <Card className="p-4 sm:p-5 mb-6">
        <h3 className="text-sm font-semibold mb-3">Generate an agreement</h3>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-1.5"><Label>Job</Label>
            <Select value={genJob} onValueChange={setGenJob}>
              <SelectTrigger data-testid="agreement-generate-job-select"><SelectValue placeholder="Select a job" /></SelectTrigger>
              <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} — {j.client_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-[2] space-y-1.5"><Label>Scope summary (optional)</Label><Input value={genScope} onChange={(e) => setGenScope(e.target.value)} data-testid="agreement-generate-scope-input" placeholder="Full website redesign incl. UX & CMS" /></div>
          <Button data-testid="agreement-generate-button" onClick={generate} disabled={!genJob}><Plus className="h-4 w-4 mr-2" />Generate</Button>
        </div>
      </Card>

      {loading ? <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      : agreements.length === 0 ? (
        <EmptyState icon={FileSignature} title="No agreements yet" description="Generate a contract from one of your jobs to get started." />
      ) : (
        <Card className="overflow-hidden">
          <Table data-testid="agreements-table">
            <TableHeader><TableRow><TableHead>Job</TableHead><TableHead className="hidden md:table-cell">Signed by</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {agreements.map((a) => (
                <TableRow key={a.id} data-testid={`agreement-row-${a.id}`} className="hover:bg-accent/50">
                  <TableCell><div className="font-medium">{a.job_title}</div><div className="text-xs text-muted-foreground max-w-[280px] truncate">{a.scope_summary}</div></TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{a.signed_by_name ? <>{a.signed_by_name}<div className="text-xs text-muted-foreground">{fmtDate(a.signed_at)}</div></> : "—"}</TableCell>
                  <TableCell>{a.status === "signed" ? <Badge className="bg-[color:var(--kpi-positive)] text-white gap-1"><Lock className="h-3 w-3" />Signed</Badge> : <Badge variant="secondary">Draft</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8" title={a.status === "signed" ? "View" : "Edit / sign"} data-testid={`agreement-open-${a.id}`} onClick={() => openEditor(a)}>{a.status === "signed" ? <Eye className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}</Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF" data-testid={`agreement-download-${a.id}`} onClick={() => dl(a)}><Download className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agreement-editor">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{signed ? <><Lock className="h-4 w-4" />Signed agreement</> : "Agreement draft"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Scope of work</Label><Textarea value={scope} onChange={(e) => setScope(e.target.value)} disabled={signed} rows={3} data-testid="agreement-scope-textarea" /></div>
              <div className="space-y-1.5"><Label>Terms & conditions</Label><Textarea value={terms} onChange={(e) => setTerms(e.target.value)} disabled={signed} rows={5} data-testid="agreement-terms-textarea" /></div>

              <div className="rounded-lg border p-4 bg-secondary/40">
                <div className="text-sm font-semibold mb-3 flex items-center gap-2"><PenLine className="h-4 w-4" />Type to sign</div>
                {signed ? (
                  <div className="text-sm">Signed by <span className="font-semibold font-display italic">{editor.signed_by_name}</span> on {fmtDate(editor.signed_at)}.</div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-1.5"><Label>Signer's full name</Label><Input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="agreement-sign-name-input" placeholder="Jane Doe" /></div>
                    <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox checked={ack} onCheckedChange={setAck} data-testid="agreement-sign-ack" className="mt-0.5" /><span className="text-muted-foreground">I confirm the details above are correct and agree to be bound by this agreement. The date will be stamped automatically.</span></label>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="agreement-download-in-editor" onClick={() => dl(editor)}><Download className="h-4 w-4 mr-2" />Download PDF</Button>
                {!signed && <Button variant="secondary" data-testid="agreement-save-draft-button" onClick={saveDraft} disabled={busy}>Save draft</Button>}
                {!signed && <Button data-testid="agreement-sign-submit-button" onClick={sign} disabled={busy}><Lock className="h-4 w-4 mr-2" />Sign & lock</Button>}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
